import React, { useEffect, useState, useRef } from 'react';
import { Globe, Search, X, Share2 } from 'lucide-react';
import { useSocialStore } from '../store/socialStore';
import { useNavigate } from 'react-router-dom';
import ProjectCard from '../components/social/ProjectCard';
import { useContextualNavigation } from '../hooks/useContextualNavigation';
import CustomSelect from '../components/common/CustomSelect';

const PAGE_SIZE = 20;

const ExplorePage: React.FC = () => {
  const navigate = useNavigate();
  const { navigateWithContext } = useContextualNavigation();
  const {
    exploreProjects,
    exploreTotalCount,
    exploreLoading,
    loadExploreProjects,
  } = useSocialStore();

  const [sort, setSort] = useState<string>('recent');
  const [skip, setSkip] = useState<number>(0);
  const [searchInput, setSearchInput] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    loadExploreProjects(sort, skip, PAGE_SIZE, searchQuery || undefined);
  }, [sort, skip, searchQuery, loadExploreProjects]);

  // Debounce search input
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setSearchQuery(searchInput);
      setSkip(0);
    }, 300);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchInput]);

  const handleProjectClick = (projectId: number) => {
    navigateWithContext(`/shared/projects/${projectId}`, 'Explore Projects');
  };

  const handleSortChange = (value: string) => {
    setSort(value);
    setSkip(0);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setSkip(0);
  };

  const handleNextPage = () => {
    setSkip((prev) => prev + PAGE_SIZE);
  };

  const handlePrevPage = () => {
    setSkip((prev) => Math.max(0, prev - PAGE_SIZE));
  };

  const isFirstPage = skip === 0;
  const isLastPage = skip + PAGE_SIZE >= exploreTotalCount;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div className="flex items-center gap-3">
          <Globe className="w-6 h-6 text-primary-600 dark:text-primary-400" aria-hidden="true" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Explore Projects
          </h1>
        </div>

        <div className="flex items-center gap-3">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden="true" />
            <input
              type="text"
              placeholder="Search projects..."
              aria-label="Search projects"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9 pr-8 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-300 focus:border-primary-300 w-48 sm:w-64"
            />
            {searchInput && (
              <button
                onClick={handleClearSearch}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Sort Dropdown */}
          <label htmlFor="sort-select" className="text-sm text-gray-600 dark:text-gray-400">
            Sort by
          </label>
          <CustomSelect
            aria-label="Sort order"
            value={sort}
            onChange={handleSortChange}
            options={[
              { value: 'recent', label: 'Recent' },
              { value: 'most_viewed', label: 'Most Viewed' },
            ]}
          />
        </div>
      </div>

      {/* Loading State */}
      {exploreLoading && (
        <div className="flex items-center justify-center py-12">
          <p className="text-gray-500 dark:text-gray-400">Loading projects...</p>
        </div>
      )}

      {/* Empty State */}
      {!exploreLoading && exploreProjects.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Globe className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {searchQuery
              ? `No projects found matching "${searchQuery}".`
              : 'No public projects yet. Be the first to share!'}
          </p>
          {!searchQuery && (
            <button
              onClick={() => navigate('/projects')}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Share2 className="w-4 h-4" />
              Share a Project
            </button>
          )}
        </div>
      )}

      {/* Project Grid */}
      {!exploreLoading && exploreProjects.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {exploreProjects.map((project) => (
              <ProjectCard
                key={project.project_id}
                project={project}
                onClick={handleProjectClick}
              />
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Showing {skip + 1}-{Math.min(skip + PAGE_SIZE, exploreTotalCount)} of {exploreTotalCount}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevPage}
                disabled={isFirstPage}
                aria-label="Previous page"
                className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Prev
              </button>
              <button
                onClick={handleNextPage}
                disabled={isLastPage}
                aria-label="Next page"
                className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ExplorePage;
